import { StateCreator } from "zustand";
import { MeetingResource } from "../api/meetings";
import { InstanceMetadata, RecurringMetadata } from "../model/meeting_metadata";
import { Meeting, fromApiMeeting } from "../model/meeting";
import { Host, fromApiHost } from "../model/host";
import { HostResource } from "../api/hosts";
import { toMeetingHostIdSchedule } from "../model/meeting_host_schedule";

export interface MeetingsSlice {
  meetings: Meeting[];
  hostsById: Record<string, Host>;
  start: Date;
  end: Date;
  isLoading: boolean;
  loadMeetings: (start: Date, end: Date, silent?: boolean) => void;
  setInstanceMetadata: (meetingId: string, metadata: InstanceMetadata) => void;
  setRecurringMetadata: (
    meetingId: string,
    metadata: RecurringMetadata
  ) => void;
}

export const createMeetingsSlice: StateCreator<
  MeetingsSlice,
  [],
  [],
  MeetingsSlice
> = (set, get) => ({
  meetings: [],
  hostsById: {},
  start: new Date(),
  end: new Date(),
  isLoading: false,
  loadMeetings: async (start: Date, end: Date, silent = false) => {
    if (!silent) set(() => ({ isLoading: true }));
    try {
      const [apiMeetings, apiHosts] = await Promise.all([
        MeetingResource.getList({
          start: start.toISOString(),
          end: end.toISOString(),
        }),
        HostResource.getList(),
      ]);
      const hosts = apiHosts.users.map(fromApiHost);
      const hostsById = hosts.reduce((acc, host) => {
        acc[host.id] = host;
        return acc;
      }, {} as Record<string, Host>);
      const meetingsRaw = apiMeetings.map((m) => fromApiMeeting(m, hostsById));
      const seen = new Set<string>();
      const meetings = meetingsRaw.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      });
      meetings.sort((a, b) => a.start.getTime() - b.start.getTime());
      set(() => ({ meetings, hostsById, start, end }));
    } finally {
      if (!silent) set(() => ({ isLoading: false }));
    }
  },
  setInstanceMetadata: async (
    meetingId: string,
    metadata: InstanceMetadata
  ) => {
    await MeetingResource.update(meetingId, {
      instanceMetadata: {
        meetingType: metadata.meetingType,
        memberVisible: metadata.memberVisible,
        hostId: metadata.host ? metadata.host.id : undefined,
        continuousRoomKey: metadata.continuousRoomKey,
      },
    });
    // Optimistic update — instant visual feedback
    set((state) => ({
      meetings: state.meetings.map((m) =>
        m.id === meetingId
          ? { ...m, host: metadata.host ?? m.host, metadata: { ...m.metadata, ...metadata } }
          : m
      ),
    }));
    // Silent background reload — no isLoading toggle, avoids race with concurrent saves
    const current = get();
    current.loadMeetings(current.start, current.end, true);
  },
  setRecurringMetadata: async (
    meetingId: string,
    metadata: RecurringMetadata
  ) => {
    await MeetingResource.update(meetingId, {
      recurringMetadata: {
        meetingType: metadata.meetingType,
        memberVisible: metadata.memberVisible,
        hostSchedule: metadata.hostSchedule
          ? toMeetingHostIdSchedule(metadata.hostSchedule)
          : undefined,
        continuousRoomKey: metadata.continuousRoomKey,
      },
    });
    // Optimistic update for recurring metadata
    set((state) => ({
      meetings: state.meetings.map((m) =>
        m.recurringMeeting?.id === meetingId
          ? {
              ...m,
              recurringMeeting: m.recurringMeeting
                ? { ...m.recurringMeeting, metadata }
                : m.recurringMeeting,
            }
          : m
      ),
    }));
    // Silent background reload — no isLoading toggle, avoids race with concurrent saves
    const current = get();
    current.loadMeetings(current.start, current.end, true);
  },
});
